<div class="deepseek-dialog">
  <button class="close-btn" mat-icon-button (click)="close()" aria-label="Close DeepSeek V3.2 info">
    <mat-icon>close</mat-icon>
  </button>

  <div class="deepseek-header">
    <img src="assets/logos/deepseek.svg" alt="DeepSeek V3.2 logo" class="deepseek-logo" />
    <h2>DeepSeek V3.2</h2>
    <div class="deepseek-sub">Ultra-efficient MoE • Long context • Strong coding + reasoning</div>
  </div>

  <div class="deepseek-body">
    <p>
      <strong>DeepSeek V3.2</strong> is a modern <strong>Mixture-of-Experts (MoE)</strong> model built to deliver
      “big-model capability” with far better compute efficiency than dense models.
      Instead of activating the entire network for every token, V3.2 selectively routes tokens through the most
      relevant expert sub-networks—so you get strong reasoning and coding quality while keeping latency and cost
      under control.
      <br /><br />
      In real workflows, DeepSeek V3.2 is especially useful for <strong>engineering-grade tasks</strong>:
      multi-step reasoning, code generation, refactoring, debugging, and long context sessions where
      the model needs to stay consistent across many turns.
    </p>

    <ul class="qualities">
      <li>
        🚀 <strong>MoE efficiency that scales:</strong>
        Selective expert activation improves throughput and cost-to-quality, making it a strong “daily driver”
        for production apps that need high capability without flagship-level spend.
      </li>

      <li>
        🧠 <strong>Reasoning that stays on-rails:</strong>
        Handles multi-step logic, constraint following, and “explain why” prompts with fewer shallow detours—
        great for planning, troubleshooting, and decision support.
      </li>

      <li>
        🧑‍💻 <strong>Engineering-first coding:</strong>
        Strong at patch-style code generation, refactors, bug diagnosis, test writing, and architecture notes—
        especially when you provide repo context, error logs, and constraints.
      </li>

      <li>
        📚 <strong>Long-context stamina:</strong>
        Built for long documents, multi-file reasoning, and extended conversations—useful for specs, PR reviews,
        incident timelines, and “read → synthesize → output” workflows.
      </li>

      <li>
        🧾 <strong>Structured outputs:</strong>
        Reliable for predictable formatting—bullets, checklists, tables, and JSON-style schemas—so downstream
        systems can parse results safely.
      </li>

      <li>
        🧩 <strong>Tool + agent workflows:</strong>
        Works well as the “planner brain” in agent loops (search → analyze → execute → verify → summarize),
        especially when paired with validation and schema checks.
      </li>
    </ul>

    <div class="optimal">
      <h3>When DeepSeek V3.2 is the best pick</h3>
      <ul>
        <li><strong>High-volume engineering support:</strong> debugging, refactors, test generation, migrations</li>
        <li><strong>Long document reasoning:</strong> specs, policies, contracts, runbooks, technical docs</li>
        <li><strong>Multi-step analysis:</strong> tradeoffs, constraint-based planning, root-cause writeups</li>
        <li><strong>Structured extraction:</strong> turning messy text into stable fields for automation</li>
        <li><strong>Production apps:</strong> strong capability with an efficiency profile that scales</li>
      </ul>
    </div>

    <div class="optimal">
      <h3>Recommended usage pattern</h3>
      <ul>
        <li><strong>Provide context:</strong> paste the relevant snippet, error, constraints, and expected output</li>
        <li><strong>Ask for a format:</strong> “return JSON”, “give a checklist”, or “show a diff + notes”</li>
        <li><strong>Add validation:</strong> schema checks, rule checks, and “verify assumptions” prompts</li>
        <li><strong>For critical work:</strong> run a second verifier pass or compare with another model</li>
      </ul>
    </div>

    <div class="tip">
      <span class="tip-icon">💡</span>
      <span>
        <strong>Pro tip:</strong> DeepSeek V3.2 performs best when you give it <strong>constraints</strong>
        (what must be true), <strong>inputs</strong> (logs/docs/code), and a <strong>target format</strong>
        (diff, checklist, JSON fields). That combo produces the most “implementation-ready” outputs.
      </span>
    </div>
  </div>
</div>
